/**
 * AI BOT ERİŞİM KONTROLÜ — "siteme hangi AI botları erişebiliyor?"
 * URL listesi × AI bot seçimi → robots.txt allowed/blocked matrisi + CSV.
 * Kayıtlı site şartı yok: rakip URL'leri de test edilir (GEO danışmanlık aracı).
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Bot, Check, X, Download, Loader2, ListPlus, ShieldQuestion } from "lucide-react";

const C = { green: "#12b76a", red: "#d92d20", amber: "#f79009" };
type BotTanim = { token: string; vendor: string; kategori: string };
type Sonuc = { botlar: BotTanim[]; satirlar: { url: string; hata: string | null; sonuclar: Record<string, { sonuc: string; kural: string | null }> }[] };

const KATEGORI_AD: Record<string, string> = { "model-egitimi": "Model Eğitimi", "ai-arama": "AI Arama Dizini", "kullanici-fetch": "Kullanıcı İsteğiyle Fetch" };
const SETLER: Record<string, (b: BotTanim) => boolean> = {
  "Çekirdek set": (b) => ["GPTBot", "ClaudeBot", "Google-Extended", "PerplexityBot", "OAI-SearchBot"].includes(b.token),
  "Model eğitimi": (b) => b.kategori === "model-egitimi",
  "AI arama": (b) => b.kategori === "ai-arama",
};

export default function AiBotErisim() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);
  const [urlMetni, setUrlMetni] = useState("");
  const [secili, setSecili] = useState<Set<string> | null>(null); // null = henüz katalogdan doldurulmadı (tümü)
  const [ara, setAra] = useState("");

  const { data: katalog } = useQuery<{ botlar: BotTanim[] }>({
    queryKey: ["ai-botlar"],
    queryFn: async () => (await fetch("/api/araclar/ai-botlar")).json(),
  });
  const botlar = katalog?.botlar ?? [];
  const seciliSet = secili ?? new Set(botlar.map((b) => b.token));

  const test = useMutation({
    mutationFn: async (): Promise<Sonuc> => {
      const r = await fetch("/api/araclar/ai-bot-erisim", {
        method: "POST", headers: { "content-type": "application/json" },
        body: JSON.stringify({ urls: urlMetni.split("\n"), botlar: [...seciliSet] }),
      });
      if (!r.ok) throw new Error(((await r.json().catch(() => ({}))) as { error?: string }).error ?? "Kontrol başarısız");
      return r.json();
    },
  });

  const sayfalariDoldur = async () => {
    if (!activeDomainId) return;
    const r = await fetch(`/api/domains/${activeDomainId}/pages`);
    if (!r.ok) return;
    const j = (await r.json()) as { pages?: { url: string }[] } | { url: string }[];
    const sayfalar = (Array.isArray(j) ? j : j.pages ?? []).map((p) => p.url).slice(0, 100);
    setUrlMetni(sayfalar.join("\n"));
  };

  const csvIndir = () => {
    const d = test.data; if (!d) return;
    const bas = ["URL", ...d.botlar.map((b) => b.token)];
    const satirlar = d.satirlar.map((s) => [s.url, ...d.botlar.map((b) => s.sonuclar[b.token]?.sonuc ?? "")]);
    const csv = [bas, ...satirlar].map((r) => r.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "ai-bot-erisim.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  const urlSayisi = urlMetni.split("\n").map((s) => s.trim()).filter(Boolean).length;
  const gorunur = botlar.filter((b) => !ara || `${b.token} ${b.vendor}`.toLowerCase().includes(ara.toLowerCase()));

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-500/10"><Bot className="h-6 w-6 text-brand-500" /></div>
        <div>
          <h1 className="text-xl font-bold">AI Bot Erişim Kontrolü</h1>
          <p className="text-sm text-muted-foreground">URL'leri gir, her sayfanın robots.txt durumunu seçili AI botları için tek tabloda gör — rakip siteler dahil.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card><CardContent className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">URL Listesi</span>
            <span className="text-[11px] tabular-nums text-muted-foreground">{urlSayisi} / 100</span>
          </div>
          <textarea value={urlMetni} onChange={(e) => setUrlMetni(e.target.value)} spellCheck={false}
            placeholder={"https://siten.com\nhttps://siten.com/blog\nhttps://rakip.com/urun/abc"}
            className="h-56 w-full rounded-lg border border-gray-200 bg-transparent p-3 font-mono text-[12.5px] leading-relaxed outline-none focus:border-brand-300 dark:border-gray-800" />
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-[11px] text-muted-foreground">Her satıra bir URL; şema yazmazsan https:// varsayılır.</span>
            {activeDomainId && <button onClick={() => void sayfalariDoldur()} className="flex items-center gap-1 text-[11.5px] font-medium text-brand-600 hover:underline"><ListPlus className="h-3.5 w-3.5" /> Sitemin sayfalarını doldur</button>}
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">AI Bot Seçimi</span>
            <span className="text-[11px] tabular-nums text-muted-foreground">{seciliSet.size} seçili</span>
          </div>
          <input value={ara} onChange={(e) => setAra(e.target.value)} placeholder="Bot ya da şirket ara (GPTBot, Anthropic…)"
            className="mb-2 h-9 w-full rounded-lg border border-gray-200 bg-transparent px-3 text-[12.5px] outline-none focus:border-brand-300 dark:border-gray-800" />
          <div className="mb-2 flex flex-wrap gap-1.5">
            <button onClick={() => setSecili(new Set(botlar.map((b) => b.token)))} className="rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-medium hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.04]">Tümünü seç</button>
            <button onClick={() => setSecili(new Set())} className="rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-medium hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.04]">Temizle</button>
            {Object.entries(SETLER).map(([ad, f]) => (
              <button key={ad} onClick={() => setSecili(new Set(botlar.filter(f).map((b) => b.token)))} className="rounded-full border border-gray-200 px-2.5 py-1 text-[11px] font-medium hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-white/[0.04]">{ad}</button>
            ))}
          </div>
          <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-100 dark:border-gray-800">
            {["model-egitimi", "ai-arama", "kullanici-fetch"].map((kat) => {
              const grup = gorunur.filter((b) => b.kategori === kat);
              if (grup.length === 0) return null;
              return (
                <div key={kat}>
                  <div className="bg-gray-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground dark:bg-white/[0.03]">{KATEGORI_AD[kat]} ({grup.length})</div>
                  {grup.map((b) => (
                    <label key={b.token} className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] hover:bg-gray-50 dark:hover:bg-white/[0.03]">
                      <input type="checkbox" checked={seciliSet.has(b.token)} onChange={(e) => {
                        const n = new Set(seciliSet);
                        e.target.checked ? n.add(b.token) : n.delete(b.token);
                        setSecili(n);
                      }} className="accent-brand-500" />
                      <span className="font-mono">{b.token}</span>
                      <span className="ml-auto text-[11px] text-muted-foreground">{b.vendor}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent></Card>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button disabled={test.isPending || urlSayisi === 0 || seciliSet.size === 0} onClick={() => test.mutate()} className="gap-1.5">
          {test.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldQuestion className="h-4 w-4" />} Test Et
        </Button>
        {test.data && <Button variant="outline" onClick={csvIndir} className="gap-1.5"><Download className="h-4 w-4" /> CSV indir</Button>}
        {test.isError && <span className="text-sm" style={{ color: C.red }}>{(test.error as Error).message}</span>}
      </div>

      {test.data && (
        <Card className="mt-4"><CardContent className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
              <thead><tr className="border-b-2 border-gray-100 dark:border-gray-800">
                <th className="px-2 py-2 text-left font-semibold">URL</th>
                {test.data.botlar.map((b) => (
                  <th key={b.token} className="px-1 py-2 text-center align-bottom">
                    <span className="inline-block whitespace-nowrap font-mono text-[10px] font-semibold [writing-mode:vertical-rl] [transform:rotate(180deg)]" title={b.vendor}>{b.token}</span>
                  </th>
                ))}
              </tr></thead>
              <tbody>
                {test.data.satirlar.map((s) => (
                  <tr key={s.url} className="border-b border-gray-50 dark:border-gray-800/60">
                    <td className="max-w-[320px] truncate px-2 py-1.5 font-mono text-[11.5px]" title={s.url}>{s.url.replace(/^https?:\/\//, "")}</td>
                    {test.data!.botlar.map((b) => {
                      const r = s.sonuclar[b.token];
                      const engelli = r?.sonuc === "blocked";
                      return (
                        <td key={b.token} className="px-1 py-1.5 text-center" title={r?.kural ?? (engelli ? "engelli" : "izinli")}>
                          {engelli ? <X className="mx-auto h-4 w-4" style={{ color: C.red }} /> : <Check className="mx-auto h-4 w-4" style={{ color: C.green }} />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">✓ izinli · ✗ robots.txt engelli — hücrenin üzerine gel: hangi kuralın karar verdiği görünür. robots.txt olmayan sitede her şey izinli sayılır (üzerine gelince belirtilir).</p>
        </CardContent></Card>
      )}
    </div>
  );
}
